/**
 * Billing - manage subscription, see status, open Stripe portal.
 *
 * Accessible whether user is subscribed or not (so they can re-subscribe).
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Crown,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Billing() {
  const { user, refreshBilling } = useAuth();
  const [params, setParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/billing/summary");
    setSummary(r.data);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (params.get("canceled")) {
      toast.info("Checkout canceled.");
      params.delete("canceled");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const portal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open portal");
      setPortalBusy(false);
    }
  };

  if (!summary) {
    return <div className="text-neutral-500 text-sm font-mono">Loading…</div>;
  }

  const reason = summary.access_reason;
  const isLifetime = reason === "lifetime";

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
          Billing
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">
          Subscription
        </h1>
      </div>

      <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
              Status
            </div>
            <div className="mt-3">
              {isLifetime ? (
                <div
                  className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 px-3 py-1.5 text-xs font-mono"
                  data-testid="billing-status-lifetime"
                >
                  <Crown className="w-3.5 h-3.5" strokeWidth={1.8} />
                  Lifetime access
                </div>
              ) : summary.has_access ? (
                <div
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 px-3 py-1.5 text-xs font-mono"
                  data-testid="billing-status-active"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                  {reason === "grace"
                    ? "Grace period (48h)"
                    : summary.cancel_at_period_end
                      ? "Active · cancels at period end"
                      : "Active"}
                </div>
              ) : (
                <div
                  className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 px-3 py-1.5 text-xs font-mono"
                  data-testid="billing-status-none"
                >
                  <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.8} />
                  Not subscribed
                </div>
              )}
            </div>
          </div>
          {!isLifetime && (
            <button
              onClick={portal}
              disabled={portalBusy || !user?.stripe_customer_id}
              data-testid="billing-open-portal"
              className="inline-flex items-center gap-2 border border-[#27272a] bg-[#0a0a0a] hover:bg-[#161616] text-sm font-mono uppercase tracking-[0.18em] text-neutral-300 rounded-md px-3 py-2 disabled:opacity-40"
            >
              {portalBusy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />
              ) : (
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.8} />
              )}
              Manage in Stripe
            </button>
          )}
        </div>

        <div className="mt-6 text-sm font-mono space-y-1.5 text-neutral-400">
          <Row label="Plan" value={summary.plan || "-"} />
          <Row label="Subscription status" value={summary.subscription_status} />
          <Row
            label="Current period ends"
            value={
              summary.current_period_end
                ? new Date(summary.current_period_end).toLocaleString()
                : "-"
            }
          />
          {summary.grace_period_end && (
            <Row
              label="Grace period ends"
              value={new Date(summary.grace_period_end).toLocaleString()}
            />
          )}
        </div>
      </div>

      {!summary.has_access && !isLifetime && (
        <a
          href="/paywall"
          data-testid="billing-resubscribe"
          className="block border border-white rounded-lg p-5 hover:bg-[#111] transition-colors text-center text-sm"
        >
          <CreditCard className="w-4 h-4 inline mr-1.5" strokeWidth={1.8} />
          Subscribe to unlock the dashboard
        </a>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-[#161616] last:border-none">
      <span className="text-neutral-500">{label}</span>
      <span className="text-neutral-300">{value}</span>
    </div>
  );
}
